import React from "react";
import { API_URL, minutesPerSlot } from "../constants";
import { timeStringToSeconds } from "../commons";
import { Room } from "./Room";
import { sprintf } from "sprintf-js";
import CalendarSession from "./CalendarSession";
import TimeSlot from "./TimeSlot";
import EditSessionModal from "./modals/EditSessionModal";
import Button from "react-bootstrap/lib/Button";
import RoomCreateModal from "./modals/RoomCreateModal";
import { ShowSessionModal } from "./modals/SessionModal";
import CalendarSessionModal from "./modals/CalendarSessionModal";
import { DeleteModal } from "./modals/DeleteModal";
import { connect } from "react-redux";
import {
    addToUnassigned,
    changeSelectedDay,
    deleteRoom,
    loadCalendar,
} from "../redux/actions";
import { getCalendarDataForDay, getRoomsForDay } from "../redux/selectors";
import { Loader } from "./Loader";
import RoomEditModal from "./modals/RoomEditModal";

class RightFrame extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            filterText: "",
            showModal: {
                RoomAdd: false,
                RoomEdit: false,
                SessionEdit: false,
                CalendarSessionShow: false,
                CalendarSessionEdit: false,
                deleteContent: false,
            }, // Controls whether to show Session Modal
            calendarSessionIdClicked: null,
            roomIdClicked: null,
        };

        this.handleClickPrevious = this.handleClickPrevious.bind(this);
        this.handleClickNext = this.handleClickNext.bind(this);
        this.handleModalShow = this.handleModalShow.bind(this);
        this.handleModalClose = this.handleModalClose.bind(this);
        this.handleRoomEditClick = this.handleRoomEditClick.bind(this);
        this.handleRoomDeleteClick = this.handleRoomDeleteClick.bind(this);
        this._deleteRoom = this._deleteRoom.bind(this);
        this.handleShowCalendarDetail =
            this.handleShowCalendarDetail.bind(this);
        this.handleShowCalendarEdit = this.handleShowCalendarEdit.bind(this);
    }

    handleDayChange(newDay) {
        this.props.changeSelectedDay(newDay);
    }

    handleClickPrevious() {
        const { daySelected } = this.props;
        if (daySelected > 1) this.handleDayChange(daySelected - 1);
    }

    handleClickNext() {
        const { daySelected, conferenceDates } = this.props;
        if (daySelected < conferenceDates.length)
            this.handleDayChange(daySelected + 1);
    }

    handleModalShow(modalName) {
        this.setState((state, props) => {
            let { showModal } = { ...state };
            showModal[modalName] = true;
            return { showModal };
        });
    }

    handleModalClose(modalName) {
        this.setState((state, props) => {
            let { showModal } = { ...state };
            showModal[modalName] = false;
            return { showModal };
        });
    }

    handleRoomEditClick(roomId, event) {
        this.setState({ roomIdClicked: roomId });
        this.handleModalShow("RoomEdit");
    }

    handleRoomDeleteClick(roomId, event) {
        this.setState({ roomIdClicked: roomId });
        this.handleModalShow("deleteContent");
    }

    /**
     * Calendar session detail button event handler
     * @param calendarSessionId
     * @param event
     */
    handleShowCalendarDetail(calendarSessionId, event) {
        this.setState({ calendarSessionIdClicked: calendarSessionId });
        this.handleModalShow("CalendarSessionShow");
    }

    /**
     * Calendar session edit button event handler
     * @param calendarSessionId
     * @param event
     */
    handleShowCalendarEdit(calendarSessionId, event) {
        this.setState({ calendarSessionIdClicked: calendarSessionId });
        this.handleModalShow("CalendarSessionEdit");
    }

    /**
     * Filters calendar sessions according to current text input
     * @param filterText
     * @param calendarSessions
     */
    filterCalendarSessions(filterText, calendarSessions) {
        const text = filterText.toLowerCase().trim();

        /* Here is where the filtering is done based on the user input */
        return calendarSessions.filter((calendarSession) => {
            if (text === "") return true;
            const session = calendarSession.session;
            const short_name = session.data.short_name ?? session.type;
            return (
                session.data.name.toLowerCase().startsWith(text) ||
                short_name.toLowerCase().startsWith(text)
            );
        });
    }

    /**
     * Performs DELETE request and removes room from the state.
     * @param roomId
     */
    _deleteRoom(roomId) {
        const { roomIdClicked } = this.state;

        fetch(API_URL + "/rooms/" + roomIdClicked, {
            method: "DELETE",
            headers: {
                Authorization: "Bearer " + this.props.user.api_token,
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        })
            .then((response) => {
                if (!response.ok) throw Error(response.statusText); // Will go to .catch
                return response.json();
            })
            .then((result) => {
                /* Delete room from the state */
                this.props.deleteRoom(roomIdClicked);

                /* Make a list of all sessions that were linked to the room that has just been removed */
                const sessionIdsToUnassign = [];
                for (const calendarSession of result)
                    sessionIdsToUnassign.push(
                        this.props.sessions[calendarSession.session_id]
                    );
                this.props.addToUnassigned(sessionIdsToUnassign);

                // Close modal
                const { showModal } = { ...this.state };
                showModal.deleteContent = false;
                this.setState({ showModal });
            })
            .catch((error) => {
                alert(error.toString());
            });
    }

    componentDidMount() {
        if (this.props.isCalendarLoaded) return;

        /* Fetch shared resources simultaneously */
        let urls = [
            API_URL + "/conference-days",
            API_URL + "/calendar",
            API_URL + "/rooms",
        ];

        // map every url to the promise of the fetch
        let requests = urls.map((url) =>
            fetch(url, {
                method: "GET",
                headers: {
                    Authorization: "Bearer " + this.props.user.api_token,
                    Accept: "application/json",
                },
            })
        );

        // Promise.all waits until all jobs are resolved
        Promise.all(requests)
            .then((responses) => {
                // all responses are resolved successfully
                for (let response of responses) {
                    if (!response.ok) throw Error(response.statusText); // Will go to .catch
                }
                return responses;
            })
            // map array of responses into an array of response.json() to read their content
            .then((responses) => Promise.all(responses.map((r) => r.json())))
            // all JSON answers are parsed: "results" is the array of them
            .then((results) => {
                this.props.loadCalendar(
                    results[0],
                    results[1].data,
                    results[2]
                );
            })
            .catch((error) => {
                alert(error);
            });
    }

    render() {
        const { filterText, calendarSessionIdClicked, roomIdClicked } =
            this.state;
        const {
            sessions,
            sessionTypes,
            user,
            conferenceDates,
            daySelected,
            roomsForDay,
            calendarDataForDay,
            isCalendarLoaded,
        } = this.props;

        if (!isCalendarLoaded) return <Loader />;

        const dayElements = conferenceDates.map((date_str, index) => {
            const pos = index + 1;
            const className = pos === daySelected ? "active" : "";
            return (
                <li key={index} className={className}>
                    <a
                        href="#"
                        className="dayp"
                        onClick={(e) => this.handleDayChange(pos)}
                    >
                        {pos}
                    </a>
                </li>
            );
        });

        /* Create rooms */
        /* Sort ascend objects by position value */
        const sortedRooms = Object.values(roomsForDay).sort(function (a, b) {
            return a.position - b.position;
        });
        const roomElements = sortedRooms.map((room) => {
            return (
                <th key={room.id}>
                    <Room
                        room={room}
                        handleEditClick={(e) =>
                            this.handleRoomEditClick(room.id, e)
                        }
                        handleRemoveClick={(e) =>
                            this.handleRoomDeleteClick(room.id, e)
                        }
                    />
                </th>
            );
        });

        /* Build an object that has grouped calendar session ids by start time and room */
        const timeToCalendarId = {};
        this.filterCalendarSessions(
            filterText,
            Object.values(calendarDataForDay)
        ).forEach((calendarSession, index) => {
            // Convert start time to seconds
            const seconds = timeStringToSeconds(calendarSession.start_time);

            // Build a multi-level object in order to map time in seconds -> rooms -> Set(calendarSessionIds)
            const roomId = calendarSession.room.id;
            if (!timeToCalendarId.hasOwnProperty(seconds))
                timeToCalendarId[seconds] = {};
            if (!timeToCalendarId[seconds].hasOwnProperty(roomId))
                timeToCalendarId[seconds][roomId] = new Set();
            timeToCalendarId[seconds][roomId].add(calendarSession.id);
        });

        /* Create calendar time slots */
        let timeSlots = [];
        for (let hour = 8; hour < 28; hour++) {
            for (let minute = 0; minute < 60; minute += minutesPerSlot) {
                let className = "calendarrow";
                if (minute % 30 === 0) className += " major";
                if (minute % 15 === 0) className += " minor";
                let timeStr = sprintf("%02d:%02d", hour % 24, minute);
                let timeInSeconds = hour * 60 * 60 + minute * 60;

                const timeSlot = (
                    <tr key={timeStr} className={className}>
                        <td className="time-label">
                            <div>{minute % 30 === 0 && timeStr}</div>
                        </td>
                        {sortedRooms.map((room, index) => {
                            /* Create calendar cells elements. Check if there exist calendar sessions for the given time and room */
                            let calendarSessions;
                            if (
                                timeToCalendarId.hasOwnProperty(
                                    timeInSeconds
                                ) &&
                                timeToCalendarId[timeInSeconds].hasOwnProperty(
                                    room.id
                                )
                            ) {
                                // Convert Set to Array
                                const ids = Array.from(
                                    timeToCalendarId[timeInSeconds][room.id]
                                );
                                calendarSessions = ids.map(
                                    (calendarSessionId) => {
                                        const calendarSession =
                                            calendarDataForDay[
                                                calendarSessionId
                                            ];
                                        console.log(calendarSession);
                                        return (
                                            <CalendarSession
                                                key={calendarSessionId}
                                                calendarSession={
                                                    calendarSession
                                                }
                                                colIdx={index}
                                                session={
                                                    sessions[
                                                        calendarSession.session
                                                            .id
                                                    ]
                                                }
                                                handleShowDetail={
                                                    this
                                                        .handleShowCalendarDetail
                                                }
                                                handleShowEdit={
                                                    this.handleShowCalendarEdit
                                                }
                                                sessionTypes={sessionTypes}
                                                user={user}
                                            />
                                        );
                                    }
                                );
                            }

                            return (
                                <TimeSlot
                                    key={timeInSeconds + "r" + room.id}
                                    roomId={room.id}
                                    dateStr={conferenceDates[daySelected - 1]}
                                    timeInSeconds={timeInSeconds}
                                    calendarSessions={calendarSessions}
                                    user={user}
                                />
                            );
                        })}
                    </tr>
                );
                timeSlots.push(timeSlot);
            }
        }

        let editSessionModal;
        if (this.state.showModal.SessionEdit) {
            const session =
                sessions[
                    calendarDataForDay[calendarSessionIdClicked].session.id
                ];
            editSessionModal = (
                <EditSessionModal
                    show={this.state.showModal.SessionEdit}
                    title={"Edit " + session.type + " Session"}
                    session={session}
                    handleClose={(e) => this.handleModalClose("SessionEdit")}
                    sessionTypes={sessionTypes}
                    selectedSessionType={session.type}
                    user={this.props.user}
                />
            );
        }
        return (
            <div id="right">
                {/*----- LEFT NAVBAR HEADER -----*/}
                <div className="filter">
                    <h4>
                        <div className="daytitle">
                            Day <span id="conferenceday">{daySelected}</span>
                        </div>
                    </h4>
                    <nav aria-label="Select day" className="daypicker">
                        <ul className="pagination pagination-sm">
                            <li>
                                <a
                                    href="#"
                                    className="dayp"
                                    aria-label="Previous"
                                    onClick={this.handleClickPrevious}
                                >
                                    <span aria-hidden="true">&laquo;</span>
                                </a>
                            </li>
                            {dayElements}
                            <li>
                                <a
                                    href="#"
                                    className="dayp"
                                    aria-label="Next"
                                    onClick={this.handleClickNext}
                                >
                                    <span aria-hidden="true">&raquo;</span>
                                </a>
                            </li>
                        </ul>
                    </nav>
                    {/*-- Input for search --*/}
                    <input
                        type="text"
                        value={this.state.filterText}
                        className="form-control"
                        placeholder="Filter by name or short name"
                        onChange={(e) =>
                            this.setState({ filterText: e.target.value })
                        }
                    />
                    {/*-- Trigger the modal with a button --*/}
                    <Button onClick={(e) => this.handleModalShow("RoomAdd")}>
                        Add Room
                    </Button>
                </div>
                <div className="scrollable" id="calendarzone">
                    <table className="schedule-table" id="calendartable">
                        <thead className="schedule-header" id="calendarheader">
                            <tr>
                                <th></th>
                                {roomElements}
                            </tr>
                        </thead>
                        <tbody className="schedule-rows" id="calendartimeslots">
                            {timeSlots}
                        </tbody>
                    </table>
                </div>

                {/*-- All modals here --*/}
                <RoomCreateModal
                    show={this.state.showModal.RoomAdd}
                    handleClose={(e) => this.handleModalClose("RoomAdd")}
                    conferenceDates={conferenceDates}
                    user={this.props.user}
                />

                {this.state.showModal.RoomEdit && (
                    <RoomEditModal
                        show={this.state.showModal.RoomEdit}
                        handleClose={(e) => this.handleModalClose("RoomEdit")}
                        room={roomsForDay[roomIdClicked]}
                        user={this.props.user}
                    />
                )}

                {this.state.showModal.CalendarSessionShow && (
                    <ShowSessionModal
                        show={this.state.showModal.CalendarSessionShow}
                        handleClose={(e) =>
                            this.handleModalClose("CalendarSessionShow")
                        }
                        session={
                            sessions[
                                calendarDataForDay[calendarSessionIdClicked]
                                    .session.id
                            ]
                        }
                        handleEditSessionClick={(e) =>
                            this.handleModalShow("SessionEdit")
                        }
                    />
                )}

                {editSessionModal}

                {this.state.showModal.CalendarSessionEdit && (
                    <CalendarSessionModal
                        show={this.state.showModal.CalendarSessionEdit}
                        handleClose={(e) =>
                            this.handleModalClose("CalendarSessionEdit")
                        }
                        calendarSession={
                            calendarDataForDay[calendarSessionIdClicked]
                        }
                        user={this.props.user}
                    />
                )}

                {this.state.showModal.deleteContent && (
                    <DeleteModal
                        show={this.state.showModal.deleteContent}
                        advise={
                            <div>
                                <br />
                                The room will be deleted for all the days and
                                its allocated sessions will return to the list
                                of unassigned.
                            </div>
                        }
                        handleClose={(e) =>
                            this.handleModalClose("deleteContent")
                        }
                        handleDeleteClick={(e) =>
                            this._deleteRoom(roomIdClicked)
                        }
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const date = state.calendar.conferenceDates[state.calendar.daySelected - 1]; // subtract one because newDay contains a 1-based index
    console.log(getCalendarDataForDay(state, date));

    return {
        conferenceDates: state.calendar.conferenceDates,
        daySelected: state.calendar.daySelected,
        calendarDataForDay: getCalendarDataForDay(state, date),
        roomsForDay: getRoomsForDay(state, date),
        isCalendarLoaded: state.calendar.isCalendarLoaded,
    };
};

export default connect(mapStateToProps, {
    loadCalendar,
    changeSelectedDay,
    deleteRoom,
    addToUnassigned,
})(RightFrame);
